import { Dropdown, Link as CarbonLink } from '@carbon/react';
import {
  DataChart,
  LagNotice,
  OverviewColumn,
  StatTile,
  useFormat,
  useOverviewQuery,
  useReferenceOptions,
  WidgetCard,
} from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { hoApi } from '../api';
import { Change } from '../reports/reportFrame';
import { REPORT_PATHS, rangeQuery, unwrap, type ReportRange } from '../reports/reportRange';
import { PERIODS, type Period } from './period';

type Summary = HoComponents['schemas']['ReportSalesSummaryDto'];
type Hourly = HoComponents['schemas']['HourlySalesDto'];
type Channel = HoComponents['schemas']['ChannelSalesDto'];
type Product = HoComponents['schemas']['ProductMixDto'];
type Tender = HoComponents['schemas']['TenderTotalsDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);
// The analytics tables follow the stores within a minute; the overview asks again every two.
const INTERVAL = 120_000;

/** The page's filters: the period and the store (or all stores), applied as soon as they change. */
export function BusinessFilters({
  period,
  storeId,
  onPeriod,
  onStore,
}: {
  period: Period;
  storeId: string;
  onPeriod(period: Period): void;
  onStore(storeId: string): void;
}) {
  const { t } = useTranslation();
  const stores = useReferenceOptions('org.store');
  const storeItems = [
    { id: '', label: t('reports.allStores') },
    ...(stores.data ?? []).map((s) => ({ id: s.value, label: s.label })),
  ];
  const periodItems = PERIODS.map((id) => ({ id, label: t(`admin.overview.biz.periods.${id}` as I18nKey) }));
  return (
    <>
      <Dropdown
        id="overview-period"
        titleText={t('admin.overview.biz.period')}
        label={t('admin.overview.biz.period')}
        items={periodItems}
        itemToString={(o) => o?.label ?? ''}
        selectedItem={periodItems.find((o) => o.id === period)}
        onChange={({ selectedItem }) => selectedItem && onPeriod(selectedItem.id)}
      />
      <Dropdown
        id="overview-store"
        titleText={t('reports.store')}
        label={t('reports.store')}
        items={storeItems}
        itemToString={(o) => o?.label ?? ''}
        selectedItem={storeItems.find((o) => o.id === storeId) ?? storeItems[0]}
        onChange={({ selectedItem }) => onStore(selectedItem?.id ?? '')}
      />
    </>
  );
}

function useReport<T>(name: string, range: ReportRange, call: () => Promise<{ data?: T; error?: unknown }>) {
  return useOverviewQuery(['business', name, range], () => unwrap<T>(call()), INTERVAL);
}

/** "Finished orders only, may lag" and a link to the full report. */
function Extra({ to }: { to: string }) {
  const { t } = useTranslation();
  return (
    <>
      <LagNotice />
      <CarbonLink as={Link} to={to} size="sm">
        {t('admin.overview.biz.openReport')}
      </CarbonLink>
    </>
  );
}

/** Sales, orders, guests, average check, discounts and voids for the period, each against the period before it. */
export function KpiTiles({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const summary = useReport<Summary>('summary', range, () =>
    hoApi.GET('/api/v1/reporting/sales/summary', { params: { query: { ...rangeQuery(range), compare: 'previous' } } }),
  );
  if (summary.isError) {
    return (
      <OverviewColumn span={16}>
        <WidgetCard
          title={t('admin.overview.biz.kpis')}
          isError
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      </OverviewColumn>
    );
  }
  const now = summary.data?.current;
  const before = summary.data?.compare ?? undefined;
  const share = (part: number, whole: number) => `${((part / whole) * 100).toFixed(1)}%`;
  const tile = (
    key: string,
    label: I18nKey,
    value: number,
    previous: number | undefined,
    text: string,
    extra?: ReactNode,
  ) => (
    <OverviewColumn key={key} span={4}>
      <StatTile
        label={t(label)}
        value={text}
        isLoading={summary.isPending}
        caption={
          // The change only when there is something to compare (both periods empty say nothing).
          previous !== undefined && (value !== 0 || previous !== 0) ? (
            <>
              <Change now={value} before={previous} /> {t('admin.overview.biz.vsPrevious')}
              {extra}
            </>
          ) : (
            extra
          )
        }
      />
    </OverviewColumn>
  );
  const sales = n(now?.total);
  const discounts = n(now?.discountTotal);
  return (
    <>
      {tile('sales', 'admin.overview.biz.netSales', sales, before && n(before.total), format.number(sales))}
      {tile(
        'orders',
        'reports.orders',
        n(now?.orderCount),
        before && n(before.orderCount),
        format.number(n(now?.orderCount)),
      )}
      {tile(
        'check',
        'reports.averageCheck',
        n(now?.averageCheck),
        before && n(before.averageCheck),
        format.number(Math.round(n(now?.averageCheck))),
      )}
      {tile(
        'guests',
        'reports.guests',
        n(now?.guestCount),
        before && n(before.guestCount),
        format.number(n(now?.guestCount)),
      )}
      {tile(
        'discounts',
        'reports.discounts',
        discounts,
        before && n(before.discountTotal),
        format.number(discounts),
        n(now?.subtotal) > 0 && discounts > 0 ? (
          <> · {t('admin.overview.biz.ofSales', { share: share(discounts, n(now?.subtotal)) })}</>
        ) : undefined,
      )}
      {tile(
        'voids',
        'reports.voids',
        n(now?.voidCount),
        before && n(before.voidCount),
        format.number(n(now?.voidCount)),
      )}
      {tile(
        'refunds',
        'reports.refunds',
        n(now?.refundTotal),
        before && n(before.refundTotal),
        format.number(n(now?.refundTotal)),
      )}
      {tile('tax', 'reports.tax', n(now?.taxTotal), before && n(before.taxTotal), format.number(n(now?.taxTotal)))}
    </>
  );
}

/**
 * The period's sales as a line against the period before: by day, or by hour when the period is one day. The previous period's
 * days sit under this period's by their place in it (day 1 under day 1).
 */
export function SalesTrend({ range, period }: { range: ReportRange; period: Period }) {
  const { t } = useTranslation();
  const format = useFormat();
  const oneDay = period === 'today';
  const summary = useReport<Summary>('summary', range, () =>
    hoApi.GET('/api/v1/reporting/sales/summary', { params: { query: { ...rangeQuery(range), compare: 'previous' } } }),
  );
  const hourly = useOverviewQuery(
    ['business', 'hourly', range],
    () => unwrap<Hourly[]>(hoApi.GET('/api/v1/reporting/sales/hourly', { params: { query: rangeQuery(range) } })),
    INTERVAL,
    oneDay,
  );
  const current = t('admin.overview.biz.thisPeriod');
  const previous = t('admin.overview.biz.previousPeriod');
  const { data, rows } = useMemo(() => {
    if (oneDay) {
      const byHour = Array.from({ length: 24 }, (_, h) =>
        (hourly.data ?? []).filter((r) => n(r.hour) === h).reduce((s, r) => s + n(r.total), 0),
      );
      const first = byHour.findIndex((v) => v > 0);
      const last = 23 - [...byHour].reverse().findIndex((v) => v > 0);
      const hours = first < 0 ? [] : byHour.map((v, h) => ({ h, v })).slice(first, last + 1);
      return {
        data: hours.map(({ h, v }) => ({ group: current, key: `${String(h).padStart(2, '0')}:00`, value: v })),
        rows: hours.map(({ h, v }) => [`${String(h).padStart(2, '0')}:00`, format.number(v)]),
      };
    }
    const days = summary.data?.days ?? [];
    const before = summary.data?.compareDays ?? [];
    const label = (date: string) => format.date(date);
    return {
      data: [
        ...days.map((d) => ({ group: current, key: label(d.businessDate), value: n(d.total) })),
        ...days.flatMap((d, i) =>
          before[i] ? [{ group: previous, key: label(d.businessDate), value: n(before[i].total) }] : [],
        ),
      ],
      rows: days.map((d, i) => [
        label(d.businessDate),
        format.number(n(d.total)),
        before[i] ? format.number(n(before[i].total)) : '—',
      ]),
    };
  }, [current, format, hourly.data, oneDay, previous, summary.data]);
  const query = oneDay ? hourly : summary;
  return (
    <WidgetCard
      title={t(oneDay ? 'admin.overview.biz.trendToday' : 'admin.overview.biz.trend')}
      headerExtra={<Extra to={REPORT_PATHS.sales} />}
      isLoading={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      isEmpty={data.length === 0}
      emptyHeading={t('admin.overview.biz.noSales')}
    >
      <DataChart
        kind="line"
        data={data}
        table={{
          headers: oneDay ? [t('reports.hours.hour'), current] : [t('reports.date'), current, previous],
          rows,
        }}
        summary={t('admin.overview.biz.trendSummary', { total: format.number(n(summary.data?.current.total)) })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}

/** Sales by channel as a donut: where the orders come from. */
export function ChannelMix({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const channels = useReport<Channel[]>('channels', range, () =>
    hoApi.GET('/api/v1/reporting/sales/channels', { params: { query: rangeQuery(range) } }),
  );
  const slices = useMemo(() => {
    const byName = new Map<string, number>();
    for (const c of channels.data ?? []) byName.set(c.channelName, (byName.get(c.channelName) ?? 0) + n(c.total));
    return [...byName].map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total);
  }, [channels.data]);
  const total = slices.reduce((s, x) => s + x.total, 0);
  return (
    <WidgetCard
      title={t('admin.overview.biz.channels')}
      headerExtra={<Extra to={REPORT_PATHS.sales} />}
      isLoading={channels.isPending}
      isError={channels.isError}
      error={channels.error}
      onRetry={() => void channels.refetch()}
      isEmpty={total === 0}
      emptyHeading={t('admin.overview.biz.noSales')}
    >
      <DataChart
        kind="donut"
        data={slices.map((s) => ({ group: s.name, key: s.name, value: s.total }))}
        table={{
          headers: [t('admin.overview.biz.channel'), t('reports.total'), t('admin.overview.biz.share')],
          rows: slices.map((s) => [s.name, format.number(s.total), `${((s.total / total) * 100).toFixed(1)}%`]),
        }}
        centerLabel={t('reports.total')}
        summary={t('admin.overview.biz.channelsSummary', { count: slices.length })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}

/** Payments by method as a donut, net of refunds. */
export function PaymentMix({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const tenders = useReport<Tender[]>('tenders', range, () =>
    hoApi.GET('/api/v1/reporting/payments/tenders', { params: { query: rangeQuery(range) } }),
  );
  const slices = [...(tenders.data ?? [])]
    .map((x) => ({ name: x.name, net: n(x.net), count: n(x.count) }))
    .filter((x) => x.net > 0)
    .sort((a, b) => b.net - a.net);
  const total = slices.reduce((s, x) => s + x.net, 0);
  return (
    <WidgetCard
      title={t('admin.overview.biz.payments')}
      headerExtra={<Extra to={REPORT_PATHS.payments} />}
      isLoading={tenders.isPending}
      isError={tenders.isError}
      error={tenders.error}
      onRetry={() => void tenders.refetch()}
      isEmpty={total === 0}
      emptyHeading={t('admin.overview.biz.noPayments')}
    >
      <DataChart
        kind="donut"
        data={slices.map((s) => ({ group: s.name, key: s.name, value: s.net }))}
        table={{
          headers: [t('admin.overview.biz.method'), t('admin.overview.biz.payments'), t('admin.overview.biz.net')],
          rows: slices.map((s) => [s.name, format.number(s.count), format.number(s.net)]),
        }}
        centerLabel={t('admin.overview.biz.net')}
        summary={t('admin.overview.biz.paymentsSummary', { count: slices.length })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}

/** The ten best sellers by net sales, as a ranking. */
export function TopItems({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const products = useReport<Product>('products', range, () =>
    hoApi.GET('/api/v1/reporting/sales/products', { params: { query: { ...rangeQuery(range), limit: 10 } } }),
  );
  const top = [...(products.data?.products ?? [])].sort((a, b) => n(b.netAmount) - n(a.netAmount)).slice(0, 10);
  return (
    <WidgetCard
      title={t('admin.overview.biz.topItems')}
      headerExtra={<Extra to={REPORT_PATHS.products} />}
      isLoading={products.isPending}
      isError={products.isError}
      error={products.error}
      onRetry={() => void products.refetch()}
      isEmpty={top.length === 0}
      emptyHeading={t('admin.overview.biz.noSales')}
    >
      <DataChart
        kind="bar"
        data={[...top].reverse().map((p) => ({ group: t('reports.total'), key: p.name, value: n(p.netAmount) }))}
        table={{
          headers: [t('admin.overview.biz.item'), t('reports.quantity'), t('reports.total')],
          rows: top.map((p) => [p.name, format.number(n(p.quantity)), format.number(n(p.netAmount))]),
        }}
        summary={t('admin.overview.biz.topItemsSummary', { name: top[0]?.name ?? '' })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}

/** Stores ranked by sales, with each one's change against the period before (all stores only). */
export function StoreRanking({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const summary = useReport<Summary>('summary', range, () =>
    hoApi.GET('/api/v1/reporting/sales/summary', { params: { query: { ...rangeQuery(range), compare: 'previous' } } }),
  );
  const stores = [...(summary.data?.stores ?? [])].sort((a, b) => n(b.total) - n(a.total)).slice(0, 10);
  const change = (now: number, before: number | null | undefined) =>
    before == null
      ? '—'
      : before === 0
        ? now === 0
          ? '0%'
          : t('reports.new')
        : `${now >= before ? '+' : ''}${(((now - before) / before) * 100).toFixed(1)}%`;
  return (
    <WidgetCard
      title={t('admin.overview.biz.stores')}
      headerExtra={<Extra to={REPORT_PATHS.sales} />}
      isLoading={summary.isPending}
      isError={summary.isError}
      error={summary.error}
      onRetry={() => void summary.refetch()}
      isEmpty={stores.length === 0}
      emptyHeading={t('admin.overview.biz.noSales')}
    >
      <DataChart
        kind="bar"
        data={[...stores]
          .reverse()
          .map((s) => ({ group: t('reports.total'), key: s.storeName || s.storeCode, value: n(s.total) }))}
        table={{
          headers: [t('reports.store'), t('reports.orders'), t('reports.total'), t('admin.overview.biz.change')],
          rows: stores.map((s) => [
            s.storeName || s.storeCode,
            format.number(n(s.orderCount)),
            format.number(n(s.total)),
            change(n(s.total), s.compareTotal == null ? null : n(s.compareTotal)),
          ]),
        }}
        summary={t('admin.overview.biz.storesSummary', {
          name: stores[0] ? stores[0].storeName || stores[0].storeCode : '',
        })}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}

/** When sales come in: weekday by hour, the busier the darker. */
export function BusyHours({ range }: { range: ReportRange }) {
  const { t } = useTranslation();
  const format = useFormat();
  const hourly = useReport<Hourly[]>('hourly', range, () =>
    hoApi.GET('/api/v1/reporting/sales/hourly', { params: { query: rangeQuery(range) } }),
  );
  const rows = hourly.data ?? [];
  const hours = [...new Set(rows.filter((r) => n(r.total) > 0).map((r) => n(r.hour)))].sort((a, b) => a - b);
  const first = hours[0] ?? 0;
  const last = hours[hours.length - 1] ?? 23;
  const span = Array.from({ length: Math.max(0, last - first + 1) }, (_, i) => first + i);
  const weekdays = [1, 2, 3, 4, 5, 6, 7];
  const day = (d: number) => t(`reports.weekdays.${d}` as I18nKey);
  const hour = (h: number) => `${String(h).padStart(2, '0')}:00`;
  const value = (d: number, h: number) =>
    rows.filter((r) => n(r.weekday) === d && n(r.hour) === h).reduce((s, r) => s + n(r.total), 0);
  return (
    <WidgetCard
      title={t('admin.overview.biz.busyHours')}
      headerExtra={<Extra to={REPORT_PATHS.hours} />}
      isLoading={hourly.isPending}
      isError={hourly.isError}
      error={hourly.error}
      onRetry={() => void hourly.refetch()}
      isEmpty={hours.length === 0}
      emptyHeading={t('admin.overview.biz.noSales')}
    >
      <DataChart
        kind="heatmap"
        data={weekdays.flatMap((d) => span.map((h) => ({ group: day(d), key: hour(h), value: value(d, h) })))}
        table={{
          headers: [t('reports.hours.hour'), ...weekdays.map(day)],
          rows: span.map((h) => [hour(h), ...weekdays.map((d) => format.number(value(d, h)))]),
        }}
        legendTitle={t('reports.total')}
        summary={t('admin.overview.biz.busyHoursSummary')}
        formatValue={(v) => format.number(v)}
      />
    </WidgetCard>
  );
}
